import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { SCROLL_OFFSET, ARTICLE_COLUMN, H2 } from "./sections";

const FAQS = [
  {
    question:
      "Is AI voice typing more accurate than Windows or Mac dictation?",
    answer:
      "Raw transcription accuracy between native OS dictation and AI voice typing tools is often close. The bigger difference is what happens after transcription: AI voice typing tools clean up grammar, punctuation, and phrasing automatically, so the output needs far less manual editing.",
  },
  {
    question:
      "Can I use Google Docs Voice Typing and an AI voice typing tool at the same time?",
    answer:
      "Yes, but there's little reason to. An AI voice typing tool that works across Google Docs, Gmail, Slack, and other apps replaces the need for an app-specific tool like Google Docs Voice Typing.",
  },
  {
    question: "Does built-in dictation work offline, and does AI voice typing?",
    answer:
      "Windows Voice Access and Mac Dictation generally support offline use for basic transcription. Availability of offline mode varies by AI voice typing tool, so check the specific app's documentation if that's a requirement for your workflow.",
  },
  {
    question:
      "Will an AI voice typing tool learn how to spell names and technical terms correctly?",
    answer:
      "With a Personal Dictionary feature, yes. You correct a name or term once, and the tool remembers the correct spelling going forward, rather than requiring the same correction every session the way built-in dictation typically does.",
  },
  {
    question: "Is there a free AI voice typing option?",
    answer:
      "Yes. Zero Typing offers a free ZT Basic plan with 15 minutes of audio transcription per week, Personal Dictionary access, and basic prompt selection, with no credit card required to start.",
  },
  {
    question: "Can I save a prompt structure I use often?",
    answer:
      "Yes. Zero Typing supports custom prompts, letting you set a structure or style that is applied automatically each time you dictate, rather than repeating the same instructions every time.",
  },
  {
    question:
      "Do I need to install anything for AI voice typing, or is it browser-based?",
    answer:
      "It depends on the tool. Zero Typing offers both native downloads (Mac, Windows via .msi, Android) and a web app, so you can choose based on whether you want a desktop-integrated experience or browser access.",
  },
];

const FAQSection = () => {
  // Single-open accordion; every item starts collapsed.
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section
      id="faqs"
      style={{ scrollMarginTop: `${SCROLL_OFFSET}px` }}
      className={ARTICLE_COLUMN}
    >
      <h2 className={H2}>Frequently Asked Questions</h2>

      <div className="mt-[25.76px]">
        {FAQS.map(({ question, answer }, index) => {
          const isOpen = openIndex === index;

          return (
            <div key={question} className="border-b-[1.07px] border-[#E6ECF5]">
              <h3>
                <button
                  type="button"
                  id={`dictation-faq-trigger-${index}`}
                  aria-expanded={isOpen}
                  aria-controls={`dictation-faq-panel-${index}`}
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="flex w-full cursor-pointer items-center justify-between gap-4 py-[18px] text-left sm:py-[21.46px]"
                >
                  <span className="font-jakarta text-[15px] font-semibold leading-[24px] tracking-[0px] text-[#0A0D16] sm:text-[16.1px] sm:leading-[26.16px]">
                    {question}
                  </span>

                  <ChevronDown
                    className={`h-[18px] w-[18px] shrink-0 text-[#3AB1F6] transition-transform duration-300 ${
                      isOpen ? "rotate-180" : "rotate-0"
                    }`}
                    strokeWidth={2.2}
                    aria-hidden="true"
                  />
                </button>
              </h3>

              {/*
                grid-rows 0fr -> 1fr animates to the answer's natural height
                without measuring it, and keeps the copy in the DOM so it
                stays indexable while collapsed.
              */}
              <div
                id={`dictation-faq-panel-${index}`}
                role="region"
                aria-labelledby={`dictation-faq-trigger-${index}`}
                className={`grid transition-all duration-300 ease-out ${
                  isOpen
                    ? "grid-rows-[1fr] opacity-100"
                    : "grid-rows-[0fr] opacity-0"
                }`}
              >
                <div className="overflow-hidden">
                  <p className="pb-[18px] pr-[34px] font-inter text-[15px] font-normal leading-[26px] tracking-[0px] text-[#0A0D16B2] sm:pb-[21.46px] sm:text-[16px] sm:leading-[29.6px]">
                    {answer}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default FAQSection;
